"use client";

import React from "react";
import { CircleCheck, Loader, ShieldCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-chart-3/10 border border-chart-3/20 rounded-md flex flex-col items-center text-center animate-in fade-in zoom-in duration-500">
                <CircleCheck className="size-16 text-chart-3 mb-4" />
                <h3 className="text-2xl font-default font-semibold text-foreground mb-2">¡Mensaje enviado!</h3>
                <p className="text-foreground font-medium text-base md:text-lg max-w-md">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-8 rounded-md">
                    Enviar otro mensaje
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-default">Nombre</FormLabel>
                            <FormControl>
                                <Input placeholder="Su nombre" {...field} className="bg-transparent border-border focus-visible:ring-primary focus-visible:border-primary rounded-md transition-all font-default" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
                <FormField control={control} name="email" rules={{
        required: "El correo electrónico es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo electrónico no válido"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-default">Correo electrónico</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="usted@empresa.com" {...field} className="bg-transparent border-border focus-visible:ring-primary focus-visible:border-primary rounded-md transition-all font-default" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>
            
            <FormField control={control} name="company" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-foreground font-default">Empresa (opcional)</FormLabel>
                        <FormControl>
                            <Input placeholder="Nombre de su empresa" {...field} className="bg-transparent border-border focus-visible:ring-primary focus-visible:border-primary rounded-md transition-all font-default" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />
            
            <FormField control={control} name="message" rules={{
      required: "Cuéntenos su situación"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-foreground font-default">¿Dónde pierde más tiempo o dinero hoy?</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Ej.: tardamos mucho en responder WhatsApp, se nos pierden llamadas, cobramos tarde…" className="min-h-[140px] bg-transparent border-border focus-visible:ring-primary focus-visible:border-primary rounded-md transition-all resize-y font-default" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <Alert variant="destructive">
                    <AlertDescription>{submitError}</AlertDescription>
                </Alert>}

            <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-chart-4 hover:bg-chart-4/90 text-black font-bold px-8 py-6 text-base md:text-lg rounded-md shadow-md transition-all hover:shadow-lg">
                    {isSubmitting ? <>
                            <Loader className="animate-spin mr-2 size-5" />
                            Enviando...
                        </> : 'Quiero mi diagnóstico'}
                </Button>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <ShieldCheck className="size-4 text-primary shrink-0" />
                    <span>Sin compromiso. Usamos sus datos solo para contactarle.</span>
                </div>
            </div>
        </Form>;
}
export default function ContactAndNextSteps() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contacto" className="py-16 md:py-24 bg-accent/30 relative overflow-hidden scroll-mt-24">
            <div className="container mx-auto px-4 md:px-6 max-w-3xl">
                <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                    <div className="text-center mb-10 md:mb-12">
                        <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
                            Cuéntenos dónde pierde su empresa
                        </h2>
                        <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto font-default">
                            Le ayudamos a encontrar dónde su empresa pierde tiempo y dinero, y le proponemos cómo resolverlo con IA en sus canales de comunicación. Complete el formulario y le contactamos a la brevedad.
                        </p>
                    </div>

                    <div className="relative rounded-md overflow-hidden shadow-lg mb-10 border border-border/50">
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685354/img/friendly-business-consultant-meeting-wit-b220ac.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685354/img/friendly-business-consultant-meeting-wit-b220ac.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685354/img/friendly-business-consultant-meeting-wit-b220ac.png-wvc-sizes" alt="Asesor de AVTECSA conversando con un cliente" className="w-full h-auto object-cover max-h-[380px]" loading="lazy" decoding="async" />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent pointer-events-none" />
                    </div>

                    <div className="bg-background rounded-md shadow-md p-6 md:p-8 lg:p-10 border border-border">
                        <FormProvider formId="contact-next-steps" sectionName="ContactAndNextSteps" defaultValues={{
            name: "",
            email: "",
            company: "",
            message: ""
          }} submitText="Quiero mi diagnóstico" successMessage="¡Gracias! Recibimos su mensaje y le contactaremos muy pronto para revisar dónde su empresa puede ahorrar.">
                            <ContactForm />
                        </FormProvider>
                    </div>
                </div>
            </div>
        </section>;
}